import * as React from "react";

import {Progress} from "./progress";

type FakeProgressProps = Omit<React.ComponentProps<typeof Progress>, "value" | "defaultValue"> & {
  /** Starts the bar at this percentage (0-100). */
  initialValue?: number;
  /** Interval between updates (ms). Smaller = smoother. */
  intervalMs?: number;
  /** The maximum value to reach while faking (won't hit 100 unless `complete` is true). */
  maxBeforeComplete?: number;
  /** When true, animates to 100% and stops. */
  complete?: boolean;
};

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function FakeProgress({
  initialValue = 0,
  intervalMs = 350,
  maxBeforeComplete = 95,
  complete = false,
  ...props
}: FakeProgressProps) {
  const [value, setValue] = React.useState(() => clamp(initialValue, 0, 100));

  React.useEffect(() => {
    setValue(clamp(initialValue, 0, 100));
  }, [initialValue]);

  React.useEffect(() => {
    if (complete) {
      // Smoothly finish to 100% when instructed.
      const id = window.setInterval(() => {
        setValue((v) => {
          const next = v + Math.max(1, (100 - v) * 0.25);
          return next >= 100 ? 100 : next;
        });
      }, 120);

      return () => window.clearInterval(id);
    }

    const cap = clamp(maxBeforeComplete, 0, 99);
    if (cap <= 0) return;

    const id = window.setInterval(() => {
      setValue((v) => {
        if (v >= cap) return v;

        // Ease out as we approach the cap, with a little randomness.
        const remaining = cap - v;
        const base = Math.max(0.4, remaining * 0.06);
        const jitter = Math.random() * 0.9;
        const step = Math.min(remaining, base + jitter);
        return clamp(v + step, 0, cap);
      });
    }, intervalMs);

    return () => window.clearInterval(id);
  }, [complete, intervalMs, maxBeforeComplete]);

  return <Progress value={value} {...props} />;
}

export {FakeProgress};
